import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  schema?: Record<string, unknown>;
  articleDate?: string;
  articleAuthor?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  schema,
  articleDate,
  articleAuthor,
}: SEOHeadProps) {
  const fullTitle = `${title} | 大满贯App`;
  const fullOgTitle = ogTitle || fullTitle;
  const fullOgDescription = ogDescription || description;
  const defaultOgImage = 'https://readdy.ai/api/search-image?query=Sports%20app%20brand%20identity%20with%20modern%20design%2C%20warm%20red%20and%20gold%20color%20scheme%2C%20minimalist%20sports%20theme%2C%20Chinese%20aesthetic%2C%20clean%20composition&width=1200&height=630&seq=og-default&orientation=landscape';

  useEffect(() => {
    const currentTitle = document.title;
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', canonicalUrl);
    else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = canonicalUrl;
      document.head.appendChild(link);
    }

    setMeta('og:title', fullOgTitle, true);
    setMeta('og:description', fullOgDescription, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', ogImage || defaultOgImage, true);
    setMeta('og:site_name', '大满贯App', true);
    setMeta('og:locale', 'zh_CN', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullOgTitle);
    setMeta('twitter:description', fullOgDescription);
    setMeta('twitter:image', ogImage || defaultOgImage);

    if (articleDate) setMeta('article:published_time', articleDate, true);
    if (articleAuthor) setMeta('article:author', articleAuthor, true);

    let scriptEl: HTMLScriptElement | null = null;
    if (schema) {
      const existingSchema = document.querySelector('script[data-schema="page"]');
      if (existingSchema) existingSchema.remove();
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.setAttribute('data-schema', 'page');
      scriptEl.textContent = JSON.stringify(schema);
      document.head.appendChild(scriptEl);
    }

    return () => {
      document.title = currentTitle;
      if (scriptEl) scriptEl.remove();
    };
  }, [fullTitle, description, keywords, canonicalUrl, fullOgTitle, fullOgDescription, ogImage, ogType, schema, articleDate, articleAuthor]);

  return null;
}